import type { ColumnDef, SortingState } from "@tanstack/react-table";
import type { ChangeEvent } from "react";

import { Button, DatePicker, FormInput, Table, TableActionsDropdown } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useGetBroadcasts,
  useGetMyAccount,
} from "@/api/queries";
import { hasAdminRights } from "@/auth/shared";
import useGetColours from "@/helpers/getColours";
import useAppStore from "@/store/appStateZustand";

import type { ParsedBroadcast } from "./types/broadcast.types";

import { parseBroadcast } from "./utils/broadcastUtils";

function BroadcastsTable() {
  const { t } = useTranslation();
  const selectedAccount = useAppStore(s => s.selectedAccount) as string;
  const navigate = useNavigate();
  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const rawOriginatorCode = myAccount.data?.originatorcode?.code;
  const originatorCode = rawOriginatorCode != null && rawOriginatorCode !== ""
    ? rawOriginatorCode
    : undefined;
  const broadcasts = useGetBroadcasts({
    ac: selectedAccount,
    oc: originatorCode,
  });
  const adminRights = hasAdminRights(myAccount?.data);
  const { highlightColour } = useGetColours();

  const [mediumName, setMediumName] = useState("");
  const [songName, setSongName] = useState("");
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [stationName, setStationName] = useState("");
  const [programName, setProgramName] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "date_aired", desc: true }]);
  const handleClear = () => {
    setMediumName("");
    setSongName("");
    setDate(undefined);
    setStationName("");
    setProgramName("");
  };

  useEffect(() => {
    if (selectedAccount) {
      void broadcasts.refetch();
    }
  }, [selectedAccount, broadcasts]);

  const rows = useMemo<ParsedBroadcast[]>(
    () => (broadcasts.data ?? [])
      .filter(broadcast =>
        broadcast.account_name === selectedAccount
        || (originatorCode !== undefined && broadcast.legacy_name === originatorCode),
      )
      .map(parseBroadcast),
    [broadcasts.data, originatorCode, selectedAccount],
  );

  const columns = useMemo((): ColumnDef<ParsedBroadcast>[] => [
    {
      accessorKey: "medium",
      header: String(t("other:medium", "Medium")),
      size: 120,
      cell: ({ row }) => row.original.medium,
    },
    {
      accessorKey: "song",
      header: String(t("other:song", "Song")),
      size: 240,
      cell: ({ row }) => row.original.song?.title ?? "",
    },
    {
      accessorKey: "date_aired",
      header: String(t("other:aired", "Aired")),
      size: 120,
      cell: ({ row }) =>
        row.original.date_aired && row.original.date_aired !== "0000-00-00"
          ? format(Date.parse(row.original.date_aired), "yyyy-MM-dd")
          : "unknown",
    },
    {
      accessorKey: "station",
      header: String(t("other:station", "Station")),
      size: 120,
      cell: ({ row }) => row.original.station ?? null,
    },
    {
      accessorKey: "program",
      header: String(t("other:program", "Program")),
      size: 120,
      cell: ({ row }) => row.original.program ?? null,
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      size: 60,
      minSize: 60,
      maxSize: 60,
      enablePinning: true,
      meta: { pinSide: "right" },
      cell: ({ row }) => {
        const canAccess = adminRights && myAccount.isSuccess && row.original.legacy_import === null;
        const goToBroadcast = (event: React.MouseEvent): void => {
          event.stopPropagation();
          if (canAccess) {
            void navigate({ to: `/broadcasts/add/${row.original.id}` });
          }
        };

        return (
          <TableActionsDropdown disabled={!canAccess}>
            <Button
              variant="dropdown"
              label={String(t("common:edit", "Edit"))}
              onClick={goToBroadcast}
            />
          </TableActionsDropdown>
        );
      },
    },
  ], [t, adminRights, myAccount.isSuccess, navigate]);

  const filteredBroadcasts = useMemo(() => {
    const broadcastData = rows ?? [];

    const songTerm = songName.trim().toLowerCase();
    const mediumTerm = mediumName.trim().toLowerCase();
    const dateStr = date ? format(date, "yyyy-MM-dd") : undefined;
    const stationTerm = stationName.trim().toLowerCase();
    const programTerm = programName.trim().toLowerCase();

    return broadcastData.filter((broadcast) => {
      if (songTerm && !broadcast.song?.title.toLowerCase().includes(songTerm)) {
        return false;
      }
      if (mediumTerm && !broadcast.medium.toLocaleLowerCase().includes(mediumTerm)) {
        return false;
      }
      if (dateStr !== undefined) {
        const dateAired = broadcast.date_aired as string | null | undefined;
        const createdDate = (dateAired != null && dateAired !== "" && dateAired !== "0000-00-00")
          ? dateAired.split("T")[0]
          : null;
        if (createdDate !== dateStr) {
          return false;
        }
      }
      if (stationTerm && !broadcast.station.toLocaleLowerCase().includes(stationTerm)) {
        return false;
      }
      if (programTerm && !broadcast.program?.toLocaleLowerCase().includes(programTerm)) {
        return false;
      }

      return true;
    });
  }, [rows, date, mediumName, programName, songName, stationName]);

  const filterContent = (
    <>
      <FormInput
        label={t("broadcasts:filter-by-medium", "Medium")}
        smallFont
        placeholder={`${t("common:filter", "Filter")} ${t("common:broadcasts-medium", "medium")}`}
        value={mediumName}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setMediumName(e.target.value)}
      />
      <FormInput
        label={t("broadcasts:filter-by-name", "Song Name")}
        smallFont
        placeholder={`${t("common:filter", "Filter")} ${t("common:broadcasts-song", "song name")}`}
        value={songName}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setSongName(e.target.value)}
      />

      <DatePicker
        label={t("broadcasts:filter-by-date", "Date")}
        smallFont
        value={date}
        onChange={setDate}
        placeholder={String(t("broadcasts:filter-date-placeholder", "Pick a date"))}
      />
      <FormInput
        label={t("broadcasts:filter-by-station-name", "Station Name")}
        smallFont
        placeholder={`${t("common:filter", "Filter")} ${t("common:broadcasts-station", "station name")}`}
        value={stationName}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setStationName(e.target.value)}
      />
      <FormInput
        label={t("broadcasts:filter-by-program", "Program")}
        smallFont
        placeholder={`${t("common:filter", "Filter")} ${t("common:broadcasts-program", "program name")}`}
        value={programName}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setProgramName(e.target.value)}
      />
    </>
  );

  const totalBroadcasts = broadcasts.data?.length ?? 0;
  const filteredCount = filteredBroadcasts.length;

  const statsBlock = useMemo(() => [
    { title: t("broadcasts:stat-total-broadcasts", "Total broadcasts"), value: totalBroadcasts, highlight: false },
    { title: t("broadcasts:stat-filtered-broadcasts", "Filtered"), value: filteredCount, highlight: true },
  ], [totalBroadcasts, filteredCount, t]);

  return (
    <div className="w-full p-8 bg-white rounded-lg">
      {!broadcasts.isError && (
        <Table
          sorting={sorting}
          onSortingChange={setSorting}
          data={filteredBroadcasts}
          statsBlock={statsBlock}
          columns={columns}
          filterContent={filterContent}
          onClear={handleClear}
          highlightColour={highlightColour}
          enableColumnResizing={false}
          isLoading={broadcasts.isLoading}
          isRefetchingData={broadcasts.isFetching}
        />
      )}
      {broadcasts.isError && (
        <div>
          <p>Error getting recent broadcasts</p>
        </div>
      )}
    </div>
  );
}

export default BroadcastsTable;
